// src/components/personal/sections/OrdersSection/utils/orderCache.ts
import { Order } from '../types/types';

interface CachedData {
  orders: Order[];
  timestamp: number;
  businessId: number | string;
}

interface CacheInfo {
  isCached: boolean;
  lastUpdated?: Date;
  expiration?: Date;
  itemsCount?: number;
}

/**
 * Класс для кеширования данных заказов
 * Используется для улучшения производительности при работе с большим количеством заказов
 */
class OrderCache {
  private cache: CachedData | null = null;
  private readonly maxAge: number; // Время жизни кеша в миллисекундах
  
  /**
   * Создает экземпляр кеша заказов
   * @param maxAgeMinutes Время жизни кеша в минутах (по умолчанию 15)
   */
  constructor(maxAgeMinutes: number = 15) {
    this.maxAge = maxAgeMinutes * 60 * 1000;
  }
  
  /**
   * Сохраняет данные заказов в кеше
   * 
   * @param orders Список заказов
   * @param businessId ID бизнеса
   */
  setOrders(orders: Order[], businessId: number | string): void {
    this.cache = {
      orders,
      timestamp: Date.now(),
      businessId
    };
    
    console.log(`Кеш обновлен: ${orders.length} заказов для бизнеса ${businessId}`);
  }
  
  /**
   * Получает данные заказов из кеша
   * 
   * @param businessId ID бизнеса
   * @returns Список заказов или null, если кеш отсутствует или недействителен
   */
  getOrders(businessId: number | string): Order[] | null {
    // Если кеш отсутствует или относится к другому бизнесу
    if (!this.cache || this.cache.businessId !== businessId) {
      return null;
    }
    
    // Проверяем, не устарел ли кеш
    if (this.isStale()) {
      console.log('Кеш устарел, требуется обновление');
      return null;
    }
    
    return this.cache.orders;
  }
  
  /**
   * Проверяет, устарел ли кеш
   * 
   * @returns true, если кеш устарел
   */
  isStale(): boolean {
    if (!this.cache) return true;
    
    const now = Date.now();
    const age = now - this.cache.timestamp;
    
    return age > this.maxAge;
  }
  
  /**
   * Получает информацию о времени жизни кеша
   * 
   * @returns Объект с информацией или null, если кеш отсутствует
   */
  getCacheInfo(): CacheInfo {
    if (!this.cache) {
      return {
        isCached: false
      };
    }
    
    // Используем непосредственно Date.now() без создания промежуточных переменных
    
    return {
      isCached: true,
      lastUpdated: new Date(this.cache.timestamp),
      expiration: new Date(this.cache.timestamp + this.maxAge),
      itemsCount: this.cache.orders.length
    };
  }
  
  /**
   * Очищает кеш
   */
  clearCache(): void {
    this.cache = null;
    console.log('Кеш очищен');
  }
  
  /**
   * Проверяет наличие данных в кеше для указанного бизнеса
   * 
   * @param businessId ID бизнеса
   * @returns true, если в кеше есть актуальные данные для бизнеса
   */
  hasValidCache(businessId: number | string): boolean {
    if (!this.cache || this.cache.businessId !== businessId) {
      return false;
    }
    
    return !this.isStale();
  }
}

// Экспортируем синглтон для использования во всем приложении
export const orderCache = new OrderCache();

// Также экспортируем класс для возможности создания нескольких экземпляров
export default OrderCache;